import './SmartOfficeCredentialsModal.scss'
import React, { useState, useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { Modal } from '../../../../../components/Modal/Modal'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { ReactComponent as ZocksLogo } from '../../../../../assets/images/ZocksIcon-white.svg'
import { useClaimExternalUserMutation, useCreateUserIntegrationMutation, useGetMeQuery } from '../../../../../store/user/user'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { useSearchParams } from 'react-router-dom'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { SmartOfficeIntegrationPrefillData } from './model'
import { SmartOfficeAlreadyConnectedModal } from './SmartOfficeAlreadyConnectedModal'

enum SmartOfficeStep {
  CREDENTIALS = 'CREDENTIALS',
  VERIFICATION = 'VERIFICATION',
}

export type SmartOfficeCredentialsFormValues = {
  siteName: string
  officeName: string
  username: string
  verificationCode?: string
}

export interface SmartOfficeCredentialsModalProps {
  show: boolean
  onSave: () => void
  onCancel: () => void
  prefillData?: SmartOfficeIntegrationPrefillData
}

export const SmartOfficeCredentialsModal: React.FC<SmartOfficeCredentialsModalProps> = ({ show, onSave, onCancel, prefillData }) => {
  const [currentStep, setCurrentStep] = useState<SmartOfficeStep>(SmartOfficeStep.CREDENTIALS)

  const defaultValues: SmartOfficeCredentialsFormValues = {
    siteName: prefillData?.siteName || '',
    officeName: prefillData?.officeName || '',
    username: prefillData?.username || '',
    verificationCode: '',
  }

  const form = useForm<SmartOfficeCredentialsFormValues>({ defaultValues })
  const { handleSubmit, reset, getValues } = form
  const accountId = useAppSelector(selectAccountId)
  const [createUserIntegration, { isLoading: isCreatingIntegration }] = useCreateUserIntegrationMutation()

  const [claimExternalUser, { isLoading: isSendingCode }] = useClaimExternalUserMutation()

  const [, setSearchParams] = useSearchParams()

  const { data: userData } = useGetMeQuery({ accountId: accountId })

  const hasSmartOfficeIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.SMARTOFFICE))
  }, [userData])

  const [hasDismissedModal, setHasDismissedModal] = useState(false)

  const showAlreadyConnectedModal = useMemo(() => hasSmartOfficeIntegration && !hasDismissedModal, [hasSmartOfficeIntegration, hasDismissedModal])

  useEffect(() => {
    setSearchParams({})
  }, [setSearchParams])

  const handleRequestVerificationCode = async (data: SmartOfficeCredentialsFormValues) => {
    try {
      await claimExternalUser({
        accountId: accountId,
        integrationName: IntegrationServiceId.SMARTOFFICE,
        payload: {
          siteName: getValues('siteName'),
          officeName: getValues('officeName'),
          username: getValues('username'),
          verificationCode: getValues('verificationCode'),
        },
      }).unwrap()

      setCurrentStep(SmartOfficeStep.VERIFICATION)
    } catch (e) {
      console.error('Failed to request verification code', e)
    }
  }

  const handleSaveWithVerification = async (data: SmartOfficeCredentialsFormValues) => {
    try {
      await createUserIntegration({
        accountId,
        integrationName: IntegrationServiceId.SMARTOFFICE,
        payload: {
          siteName: data.siteName,
          officeName: data.officeName,
          username: data.username,
          verificationCode: data.verificationCode,
          connectType: 'smartoffice',
        },
      }).unwrap()
      onSave()
      reset(defaultValues)
      setCurrentStep(SmartOfficeStep.CREDENTIALS)
    } catch (e) {
      console.error('Failed to create user integration', e)
    }
  }

  const handleOnHide = () => {
    reset(defaultValues)
    setCurrentStep(SmartOfficeStep.CREDENTIALS)
    onCancel()
  }

  useEffect(() => {
    if (prefillData) {
      reset(prefillData)
    }
  }, [prefillData, reset])

  useEffect(() => {
    if (!show) {
      return
    }
    if (prefillData) {
      handleSubmit(handleRequestVerificationCode)()
    }
  }, [prefillData, show])

  const handleConnectAnyway = () => {
    setHasDismissedModal(true)
  }

  if (showAlreadyConnectedModal) {
    return (
      <SmartOfficeAlreadyConnectedModal
        show={showAlreadyConnectedModal && show}
        onClose={() => setHasDismissedModal(true)}
        onConnectAnyway={handleConnectAnyway}
      />
    )
  }
  const body = (
    <Container>
      <Row className='zocks-pb-2'>
        <Stack direction='horizontal' className='d-flex justify-content-center align-items-center' gap={3}>
          <div className='zocks-bg-color-primary d-flex zocks-p-0.375 justify-content-center align-items-center zocks-border-0.25'>
            <ZocksLogo width='3rem' height='3rem' />
          </div>
          <i className='bi bi-arrow-left-right'></i>
          <div className='smartoffice-bg-color-primary d-flex zocks-p-0.375 justify-content-center align-items-center zocks-border-0.25'>
            <IntegrationIcon serviceId={IntegrationServiceId.SMARTOFFICE} width='3rem' height='3rem' fill='white' />
          </div>
        </Stack>
      </Row>
      <Row className='pb-3'>
        <Col>
          <h3 className='d-flex justify-content-center align-items-center'>
            {currentStep === SmartOfficeStep.CREDENTIALS ? 'Connect SmartOffice' : 'Enter Verification Code'}
          </h3>
        </Col>
      </Row>
      <Row className='pb-3'>
        <Col>
          <p className='d-flex justify-content-center align-items-center text-center'>
            {currentStep === SmartOfficeStep.CREDENTIALS
              ? 'Please enter your SmartOffice credentials.'
              : `A verification code has been sent related to your SmartOffice user ${getValues('username') || ''}. Please enter it below.`}
          </p>
        </Col>
      </Row>
      <Row>
        <FormProvider {...form}>
          <SmartOfficeCredentialsForm currentStep={currentStep} />
        </FormProvider>
      </Row>
    </Container>
  )

  const footer = (
    <div className='d-flex gap-3'>
      <Button variant='secondary' onClick={handleOnHide}>
        Cancel
      </Button>
      {currentStep === SmartOfficeStep.CREDENTIALS && (
        <Button variant='primary' onClick={handleSubmit(handleRequestVerificationCode)} loading={isSendingCode}>
          Send Code
        </Button>
      )}
      {currentStep === SmartOfficeStep.VERIFICATION && (
        <Button variant='primary' onClick={handleSubmit(handleSaveWithVerification)} loading={isCreatingIntegration}>
          Connect
        </Button>
      )}
    </div>
  )

  return <Modal show={show} onHide={handleOnHide} header={<></>} body={body} footer={footer} centered={true} />
}

interface SmartOfficeCredentialsFormProps {
  currentStep: SmartOfficeStep
}

const CredentialsStep: React.FC = () => {
  const { control } = useFormContext<SmartOfficeCredentialsFormValues>()

  return (
    <>
      <Row>
        <Col>
          <Controller
            name='siteName'
            control={control}
            rules={{ required: 'Site name is required.' }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label>Site name</Form.Label>
                <Form.Control id={field.name} type='text' placeholder='Enter Site name' {...field} isInvalid={!!fieldState.error} autoFocus />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Controller
            name='officeName'
            control={control}
            rules={{ required: 'Office name is required.' }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label>Office name</Form.Label>
                <Form.Control id={field.name} type='text' placeholder='Enter Office name' {...field} isInvalid={!!fieldState.error} />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Controller
            name='username'
            control={control}
            rules={{ required: 'Username is required.' }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label>Username</Form.Label>
                <Form.Control id={field.name} type='text' placeholder='Enter Username' {...field} isInvalid={!!fieldState.error} />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
    </>
  )
}

const uuid4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const VerificationStep: React.FC = () => {
  const { control } = useFormContext<SmartOfficeCredentialsFormValues>()

  return (
    <Row>
      <Col>
        <Controller
          name='verificationCode'
          control={control}
          rules={{
            required: 'Verification code is required.',
            pattern: {
              value: uuid4Regex,
              message: 'Enter a valid verification code.',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <Form.Label>Verification Code</Form.Label>
              <Form.Control id={field.name} type='text' placeholder='Enter verification code' {...field} isInvalid={!!fieldState.error} autoFocus />
              {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
            </>
          )}
        />
      </Col>
    </Row>
  )
}

export const SmartOfficeCredentialsForm: React.FC<SmartOfficeCredentialsFormProps> = ({ currentStep }) => {
  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {currentStep === SmartOfficeStep.CREDENTIALS && <CredentialsStep />}
      {currentStep === SmartOfficeStep.VERIFICATION && <VerificationStep />}
    </Container>
  )
}
